import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, ImagePlus, Loader2, Star, X } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";

const TEXT_FIELDS = [
  ["name", "Name"],
  ["tagline", "Tagline"],
  ["difficulty", "Difficulty"],
  ["duration", "Duration"],
  ["distance", "Distance"],
  ["group_size", "Group size"],
  ["price", "Price"],
  ["season", "Season"],
];

async function uploadImage(file) {
  const fd = new FormData();
  fd.append("file", file);
  const { data } = await api.post("/admin/uploads", fd);
  return data.url;
}

function UploadButton({ onUrl, testid, label = "Upload" }) {
  const [busy, setBusy] = useState(false);
  async function pick(e) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setBusy(true);
    try {
      onUrl(await uploadImage(file));
      toast.success("Photo uploaded");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  }
  return (
    <label
      className={`flex w-fit cursor-pointer items-center gap-2 rounded-full border border-ink/25 px-4 py-2 text-sm uppercase tracking-[0.15em] text-ink/70 transition-colors duration-300 hover:border-terra hover:text-terra ${busy ? "opacity-50" : ""}`}
    >
      {busy ? <Loader2 size={13} className="animate-spin" /> : <ImagePlus size={13} />}
      {busy ? "Uploading…" : label}
      <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={pick} data-testid={testid} disabled={busy} />
    </label>
  );
}

function HikeEditor({ hike, onSaved }) {
  const [form, setForm] = useState({ ...hike, included: (hike.included || []).join("\n") });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  async function save() {
    setSaving(true);
    try {
      const payload = { ...form, included: form.included.split("\n").map((s) => s.trim()).filter(Boolean) };
      delete payload.slug;
      delete payload.sort_order;
      const { data } = await api.put(`/admin/hikes/${hike.slug}`, payload);
      toast.success(`${data.name} saved — live on the site`);
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  }

  const inputCls =
    "w-full border border-ink/20 bg-white px-4 py-3 text-base text-ink outline-none transition-[border-color,box-shadow] duration-300 focus:border-terra focus:ring-2 focus:ring-terra/15";

  return (
    <div className="border-t border-ink/10 bg-olive-pale/40 px-5 py-8 sm:px-8" data-testid={`editor-${hike.slug}`}>
      <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
        {TEXT_FIELDS.map(([key, label]) => (
          <label key={key} className="block">
            <span className="mb-1.5 block text-sm uppercase tracking-[0.18em] text-ink/50">{label}</span>
            <input value={form[key] || ""} onChange={(e) => set(key, e.target.value)} data-testid={`edit-${key}-${hike.slug}`} className={inputCls} />
          </label>
        ))}
      </div>

      <label className="mt-5 block">
        <span className="mb-1.5 block text-sm uppercase tracking-[0.18em] text-ink/50">Description</span>
        <textarea rows={4} value={form.description || ""} onChange={(e) => set("description", e.target.value)} data-testid={`edit-description-${hike.slug}`} className={inputCls} />
      </label>

      <label className="mt-5 block">
        <span className="mb-1.5 block text-sm uppercase tracking-[0.18em] text-ink/50">What's included — one per line</span>
        <textarea rows={4} value={form.included} onChange={(e) => set("included", e.target.value)} data-testid={`edit-included-${hike.slug}`} className={inputCls} />
      </label>

      <div className="mt-6 grid gap-8 lg:grid-cols-2">
        <div>
          <span className="mb-2 block text-sm uppercase tracking-[0.18em] text-ink/50">Main photo</span>
          <div className="flex items-center gap-4">
            {form.image && <img src={form.image} alt="" className="h-20 w-32 border border-ink/15 object-cover" data-testid={`edit-image-preview-${hike.slug}`} />}
            <UploadButton testid={`edit-image-upload-${hike.slug}`} label={form.image ? "Replace photo" : "Upload photo"} onUrl={(url) => set("image", url)} />
          </div>
        </div>
        <div>
          <span className="mb-2 block text-sm uppercase tracking-[0.18em] text-ink/50">Gallery photos</span>
          <div className="flex flex-wrap items-center gap-3">
            {(form.gallery || []).map((src, i) => (
              <span key={src + i} className="relative">
                <img src={src} alt="" className="h-20 w-28 border border-ink/15 object-cover" />
                <button
                  type="button"
                  onClick={() => set("gallery", form.gallery.filter((_, j) => j !== i))}
                  data-testid={`edit-gallery-remove-${hike.slug}-${i}`}
                  className="absolute -right-2 -top-2 flex h-5 w-5 items-center justify-center rounded-full bg-ink text-paper transition-colors duration-300 hover:bg-terra"
                  aria-label="Remove photo"
                >
                  <X size={11} />
                </button>
              </span>
            ))}
            <UploadButton testid={`edit-gallery-upload-${hike.slug}`} label="Add photo" onUrl={(url) => set("gallery", [...(form.gallery || []), url])} />
          </div>
        </div>
      </div>

      <div className="mt-7 flex flex-wrap items-center justify-between gap-4">
        <label className="flex cursor-pointer items-center gap-3 text-base text-ink/70">
          <input
            type="checkbox"
            checked={!!form.featured}
            onChange={(e) => set("featured", e.target.checked)}
            data-testid={`edit-featured-${hike.slug}`}
            className="h-4 w-4 accent-terra"
          />
          <Star size={14} className={form.featured ? "fill-terra text-terra" : "text-ink/40"} />
          Featured on the homepage
        </label>
        <button
          onClick={save}
          disabled={saving}
          data-testid={`edit-save-${hike.slug}`}
          className="rounded-full bg-terra px-8 py-3 text-sm uppercase tracking-[0.18em] text-paper transition-[background-color,opacity] duration-300 hover:bg-terra-dark disabled:opacity-50"
        >
          {saving ? "Saving…" : "Save changes"}
        </button>
      </div>
    </div>
  );
}

function HeroEditor() {
  const [hero, setHero] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/settings").then((r) => setHero(r.data.hero_image || "")).catch(() => setHero(""));
  }, []);

  async function save(url) {
    setSaving(true);
    try {
      await api.put("/admin/settings", { hero_image: url });
      setHero(url);
      toast.success("Homepage hero updated — live immediately");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  }

  return (
    <section data-testid="hero-editor" className="mt-8 rounded-2xl border border-ink/15 bg-white p-6 sm:p-8">
      <h2 className="font-serif text-2xl tracking-tight">Homepage hero</h2>
      <p className="mt-2 text-base leading-relaxed text-ink/55">
        The full-screen photo behind the Salt &amp; Stone title. Choose a bright, wide landscape shot for the best result.
      </p>
      <div className="mt-5 flex flex-wrap items-center gap-5">
        {hero && (
          <img src={hero} alt="Current homepage hero" data-testid="hero-preview"
            className="h-24 w-44 rounded-xl border border-ink/10 object-cover" />
        )}
        <div className={saving ? "pointer-events-none opacity-50" : ""}>
          <UploadButton testid="hero-upload" label={hero ? "Replace hero photo" : "Upload hero photo"} onUrl={save} />
        </div>
      </div>
    </section>
  );
}

export default function AdminHikes() {
  const [user, setUser] = useState(null);
  const [hikes, setHikes] = useState(null);
  const [open, setOpen] = useState(null);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/hikes");
      setHikes(data);
    } catch {
      setHikes([]);
    }
  }, []);

  useEffect(() => {
    document.title = "Edit Hikes — Salt & Stone Admin";
    api.get("/auth/me")
      .then((r) => {
        setUser(r.data);
        load();
      })
      .catch(() => navigate("/admin/login"));
  }, [navigate, load]);

  if (!user) return <main className="min-h-screen bg-paper" data-testid="admin-hikes-loading" />;

  return (
    <main data-testid="admin-hikes-page" className="min-h-screen bg-paper">
      <header className="border-b border-ink/10">
        <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-between gap-4 px-5 py-5 sm:px-8">
          <div>
            <p className="font-serif text-2xl tracking-tight">
              Salt <span className="text-terra">&amp;</span> Stone
            </p>
            <p className="text-sm uppercase tracking-[0.2em] text-ink/45">Hike editor</p>
          </div>
          <Link
            to="/admin"
            data-testid="admin-back-to-bookings"
            className="flex items-center gap-2 rounded-full border border-ink/20 px-4 py-2 text-sm uppercase tracking-[0.15em] text-ink/70 transition-colors duration-300 hover:border-terra hover:text-terra"
          >
            <ArrowLeft size={13} /> Bookings
          </Link>
        </div>
      </header>

      <div className="mx-auto max-w-6xl px-5 py-10 sm:px-8">
        <p className="text-base text-ink/55">
          Edit any detail of a hike — text, prices, season, homepage featuring — and upload Vasilis's own photos.
          Changes go live immediately.
        </p>

        <HeroEditor />

        {!hikes ? (
          <p className="mt-14 text-ink/50">Loading hikes…</p>
        ) : (
          <div className="mt-8 overflow-hidden rounded-2xl border border-ink/15" data-testid="admin-hikes-list">
            {hikes.map((hike) => (
              <div key={hike.slug} className="border-b border-ink/10 last:border-b-0">
                <button
                  onClick={() => setOpen(open === hike.slug ? null : hike.slug)}
                  data-testid={`hike-edit-toggle-${hike.slug}`}
                  className="flex w-full items-center gap-4 px-5 py-4 text-left transition-colors duration-200 hover:bg-olive-pale/40 sm:px-8"
                >
                  <img src={hike.image} alt="" className="hidden h-14 w-20 shrink-0 border border-ink/10 object-cover sm:block" />
                  <span className="flex-1">
                    <span className="flex items-center gap-2 font-medium">
                      {hike.name}
                      {hike.featured && <Star size={13} className="fill-terra text-terra" />}
                    </span>
                    <span className="mt-0.5 block text-sm text-ink/50">
                      {hike.price} · {hike.duration} · {hike.season || "Season not set"}
                    </span>
                  </span>
                  <span className="text-sm uppercase tracking-[0.18em] text-terra">
                    {open === hike.slug ? "Close" : "Edit"}
                  </span>
                </button>
                {open === hike.slug && <HikeEditor hike={hike} onSaved={load} />}
              </div>
            ))}
          </div>
        )}
      </div>
    </main>
  );
}
